import { getLocalStorageUserSha } from '@/utils/storage';
import { HOUR_MS } from '@calm/web-common/dist/time';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery, invalidateQueryKey } from '@rhg/libs/react-query';
import { useCallback } from 'react';
import { RegistrationFlow } from '../use-registration-context/types';

const PreviousRegistrationFlowKey = 'prf';

interface PreviousRegistrationFlowData {
    localStorageKey: string | null;
    previousRegistrationFlow: RegistrationFlow | null;
}
const placeholderData = {
    localStorageKey: null,
    previousRegistrationFlow: null,
};

function getQueryKey(userId?: string) {
    return [queryKeys.previousRegistrationFlow, userId];
}

export function usePreviousRegistrationFlow() {
    const { isAuthenticated, user } = useAuth();

    const { data, isFetched, ...otherProps } =
        useQuery<PreviousRegistrationFlowData>({
            queryKey: getQueryKey(user?.sub),
            queryFn: async () => {
                if (isAuthenticated && user?.sub) {
                    const localStorageKey = await getLocalStorageUserSha(
                        PreviousRegistrationFlowKey,
                        user?.sub
                    );

                    // eslint-disable-next-line no-restricted-syntax
                    const localStorageItem = localStorage.getItem(
                        localStorageKey
                    ) as RegistrationFlow;

                    const previousRegistrationFlow = Object.values(
                        RegistrationFlow
                    ).includes(localStorageItem)
                        ? localStorageItem
                        : null;

                    return { localStorageKey, previousRegistrationFlow };
                }
                return placeholderData;
            },
            enabled: isAuthenticated && !!user?.sub,
            staleTime: 8 * HOUR_MS,
        });

    const setPreviousRegistrationFlow = useCallback(
        async (value: RegistrationFlow) => {
            if (user?.sub) {
                const localStorageKey = await getLocalStorageUserSha(
                    PreviousRegistrationFlowKey,
                    user.sub
                );

                // eslint-disable-next-line no-restricted-syntax
                localStorage.setItem(localStorageKey, value);

                /** Setting query data doesnt refresh the previousRegistrationFlow value, leaving it undefined **/
                // queryClient.setQueryData(getQueryKey(user.sub), `${value}`);

                /* Invalidate the query so it refetches */
                await invalidateQueryKey(getQueryKey(user.sub));
                return;
            }
            throw new Error(
                'setPreviousRegistrationFlow: hook must be fetched before it can be called'
            );
        },
        [user?.sub]
    );

    const clearPreviousRegistrationFlow = useCallback(async () => {
        if (user?.sub) {
            const localStorageKey = await getLocalStorageUserSha(
                PreviousRegistrationFlowKey,
                user.sub
            );

            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(localStorageKey);
            // queryClient.setQueryData(getQueryKey(user.sub), null);

            /** Invalidate the query so it refetches **/
            await invalidateQueryKey(getQueryKey(user.sub));
            return;
        }
        throw new Error(
            'clearPreviousRegistrationFlow: hook must be fetched before it can be called'
        );
    }, [user?.sub]);

    return {
        ...otherProps,
        ...(data ?? placeholderData),
        isFetched,
        /** Sets the previous registration flow we must wait for hook isFetched to be true */
        setPreviousRegistrationFlow,
        /** Clears the previous registration flow we must wait for hook isFetched to be true */
        clearPreviousRegistrationFlow,
    };
}
